import clsx from 'clsx'
import { useEffect, useState } from 'react'

import AllowanceTabs, { type AllowanceTab } from 'components/quoting/quotes/flat_allowances/AllowanceTabs'
import { UnderlinedTooltip } from 'components/ui/UnderlinedTooltip'

import { emitEmployerOnboardingEvent } from '../../utils'
import { COVERAGE_LEVEL_OPTIONS, DEPENDENT_COVERAGE_PERCENTAGES } from '../consts'
import { useSelfServeExplorerContext } from '../QuoteSelfServeExplorerContext'
import { type CoverageLevel } from '../types'
import { AdvancedOptions } from './AdvancedOptions'
import { CoverageLevelSelector } from './CoverageLevelSelector'

export interface FiltersValues {
  coverageLevel: CoverageLevel
  employerSpouseContribution: number
  employerChildContribution: number
}

export function DynamicQuoteOptions () {
  const {
    filterValues,
    setFilterValues,
    selectedCoverageLevel,
    onSelectedCoverageLevelChange,
    resetFilterValues,
  } = useSelfServeExplorerContext()
  const [employerDependentCoverage, setEmployerDependentCoverage] = useState<number>(filterValues.employerSpouseContribution)

  useEffect(() => {
    setEmployerDependentCoverage(filterValues.employerSpouseContribution)
  }, [filterValues.employerSpouseContribution])

  function notifyChange (
    newCoverageLevel = selectedCoverageLevel,
    newEmployerSpouseContribution = employerDependentCoverage,
    newEmployerChildContribution = employerDependentCoverage
  ) {
    setFilterValues({
      coverageLevel: newCoverageLevel,
      employerEmployeeContribution: filterValues.employerEmployeeContribution,
      employerSpouseContribution: newEmployerSpouseContribution,
      employerChildContribution: newEmployerChildContribution,
    })
  }

  const selectedOption = COVERAGE_LEVEL_OPTIONS.find(opt => opt.value === selectedCoverageLevel)
  const dependentCoverageOptions = new Set<number>([...DEPENDENT_COVERAGE_PERCENTAGES, Math.round(employerDependentCoverage * 100)].sort((a, b) => a - b))

  const tabs: AllowanceTab[] = [
    {
      id: 'recommended',
      label: 'Recommended',
      content: (
        <div className='flex flex-col gap-2'>
          <CoverageLevelSelector
            selectedCoverageLevel={selectedCoverageLevel}
            onSelectedCoverageLevelChange={onSelectedCoverageLevelChange}
            notifyChange={notifyChange}
            coverageLevelOptions={COVERAGE_LEVEL_OPTIONS}
            selectedOption={selectedOption}
            resetFilterValues={resetFilterValues}
          />
          <div className='flex flex-col md:flex-row md:items-center gap-4 mt-6'>
            <h2 className='heading-sm mr-6'>
              <UnderlinedTooltip content='The percentage of health insurance cost you&apos;ll cover for your employees&apos; dependents' className='w-fit'>
                Dependent coverage
              </UnderlinedTooltip>
            </h2>
            <div className='flex-1'>
              <div className='flex flex-row rounded-2xl border border-gray-200 overflow-hidden bg-white p-1'>
                {[...dependentCoverageOptions].map((percentage: number) => (
                  <button
                    key={percentage}
                    type='button'
                    className={clsx(
                      'flex-1 text-center transition-all duration-200',
                      'flex items-center justify-center px-2 py-2',
                      'body',
                      Math.round(employerDependentCoverage * 100) === percentage
                        ? 'bg-thatch-100 border border-thatch-500 text-gray-800 font-semibold rounded-xl'
                        : 'bg-transparent text-gray-700 hover:bg-gray-50'
                    )}
                    onClick={() => {
                      const newValue = percentage / 100
                      setEmployerDependentCoverage(newValue)
                      notifyChange(selectedCoverageLevel, newValue, newValue)
                      emitEmployerOnboardingEvent('[ER] AllowanceTool dependent coverage selected', {})
                    }}
                  >
                    {percentage}%
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'custom',
      label: 'Custom',
      content: (
        <div className='flex flex-col gap-2'>
          <CoverageLevelSelector
            selectedCoverageLevel={selectedCoverageLevel}
            onSelectedCoverageLevelChange={onSelectedCoverageLevelChange}
            notifyChange={notifyChange}
            coverageLevelOptions={COVERAGE_LEVEL_OPTIONS}
            selectedOption={selectedOption}
            resetFilterValues={resetFilterValues}
          />
          <div className='mt-6'>
            <AdvancedOptions setDependentCoverage={setEmployerDependentCoverage} />
          </div>
        </div>
      )
    }
  ]

  return (
    <AllowanceTabs
      defaultValue='recommended'
      tabs={tabs}
      onValueChange={(value: string) => {
        if (value === 'recommended') {
          emitEmployerOnboardingEvent('[ER] AllowanceTool recommended strategy selected', {})
        } else if (value === 'custom') {
          emitEmployerOnboardingEvent('[ER] AllowanceTool custom strategy selected', {})
        }
      }}
    />
  )
}
